import type { Ref } from 'vue'
import { gql } from '@apollo/client/core'

import { UnsupportedActionError, clampMax } from '../../lib/boardInvariants'
import {
  GroupRenameDocument,
  GroupSetMaxPlayersDocument,
} from '@/shared/api/gql/graphql'
import type { GroupId, RoomId } from '../../types'

export { UnsupportedActionError }

const GroupSetCollapsedDocument = gql`
  mutation GroupSetCollapsed($guildId: Int!, $groupId: Int!, $collapsed: Boolean!) {
    groupSetCollapsed(guildId: $guildId, groupId: $groupId, collapsed: $collapsed)
  }
`

export interface GroupRoomActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly run: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ) => Promise<void>
  readonly gid: (id: GroupId) => number
}

export interface GroupRoomActionsApi {
  readonly addGroup: (roomId: RoomId) => Promise<GroupId | null>
  readonly removeGroup: (groupId: GroupId) => Promise<void>
  readonly renameGroup: (groupId: GroupId, label: string) => void
  readonly setMaxPlayers: (groupId: GroupId, max: number) => void
  readonly setGroupCollapsed: (groupId: GroupId, collapsed: boolean) => void
}

export function useGroupRoomActions(ctx: GroupRoomActionsCtx): GroupRoomActionsApi {
  function addGroup(_roomId: RoomId): Promise<GroupId | null> {
    const err = new UnsupportedActionError('addGroup')
    ctx.error.value = err.message
    return Promise.reject(err)
  }

  function removeGroup(_groupId: GroupId): Promise<void> {
    const err = new UnsupportedActionError('removeGroup')
    ctx.error.value = err.message
    return Promise.reject(err)
  }

  function renameGroup(groupId: GroupId, label: string): void {
    void ctx.run(GroupRenameDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      groupId: ctx.gid(groupId),
      label,
    })
  }

  function setMaxPlayers(groupId: GroupId, max: number): void {
    void ctx.run(GroupSetMaxPlayersDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      groupId: ctx.gid(groupId),
      max: clampMax(max),
    })
  }

  function setGroupCollapsed(groupId: GroupId, collapsed: boolean): void {
    void ctx.run(GroupSetCollapsedDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      groupId: ctx.gid(groupId),
      collapsed,
    })
  }

  return {
    addGroup,
    removeGroup,
    renameGroup,
    setMaxPlayers,
    setGroupCollapsed,
  }
}
